import type { Product } from '../types';
import { brandifyText } from '../lib/utils';

/**
 * Display-only brand presentation: wrap the brand token in guillemets
 * («X-1493») for every product fetched, so all customer-facing pages
 * (Store / Home / ProductDetail / Cart / Checkout) show it consistently.
 * Stored data (D1, SKU, slugs) is untouched.
 */
function brandifyProduct(p: Product): Product {
  return {
    ...p,
    name: p.name ? brandifyText(p.name) : p.name,
    description: p.description ? brandifyText(p.description) : p.description,
    brand: p.brand ? brandifyText(p.brand) : p.brand,
  };
}

export const DEMO_PRODUCTS: Product[] = [
  {
    id: 1,
    sku: 'X1493-001',
    name: '入門辣粉',
    slug: 'starter-spice',
    description: '溫和開胃，適合初次嘗試港式辣味的朋友。以優質辣椒研磨，香氣撲鼻而不嗆喉，讓你輕鬆踏出辣味之旅的第一步。',
    brandGroup: 'hk_brand',
    brand: 'main',
    category: 'spice',
    price: 9900,
    originalPrice: 12000,
    stockQuantity: 50,
    imageUrl: '/media/media/dishes/i1.jpg',
    spicinessLevel: 1,
    isActive: 1,
    sortOrder: 1,
    suitableDishes: ['炒飯', '湯麵', '蒸魚'],
  },
  {
    id: 2,
    sku: 'X1493-002',
    name: '中辣辣粉',
    slug: 'medium-spice',
    description: '層次豐富的中辣風味，帶有微微的果香與煙燻感。無論是避風塘炒蟹還是車仔麵，都能讓菜式更上一層樓。',
    brandGroup: 'hk_brand',
    brand: 'main',
    category: 'spice',
    price: 11900,
    originalPrice: 14000,
    stockQuantity: 40,
    imageUrl: '/media/media/dishes/i2.jpg',
    spicinessLevel: 3,
    isActive: 1,
    sortOrder: 2,
    suitableDishes: ['避風塘炒蟹', '車仔麵', '咖喱魚蛋'],
  },
  {
    id: 3,
    sku: 'X1493-003',
    name: '地獄辣套裝',
    slug: 'hell-spice-set',
    description: '為真正的辣味冒險者準備。內含地獄級辣粉與特製辣油，挑戰你的味覺極限。適合敢於追求火熱的港式辣魂。',
    brandGroup: 'hk_brand',
    brand: 'main',
    category: 'set',
    price: 24900,
    originalPrice: 29900,
    stockQuantity: 25,
    imageUrl: '/media/media/dishes/i3.jpg',
    spicinessLevel: 5,
    isActive: 1,
    sortOrder: 3,
    suitableDishes: ['麻辣火鍋', '香辣雞煲', '地獄拉麵'],
  },
];

export async function fetchProducts(): Promise<Product[]> {
  try {
    const res = await fetch('/api/public/products');
    if (!res.ok) return DEMO_PRODUCTS.map(brandifyProduct);
    const json = await res.json();
    if (json.success && Array.isArray(json.data) && json.data.length > 0) {
      return json.data.map((p: Product) => brandifyProduct({
        ...p,
        name: p.name || `商品 #${p.id}`,
        slug: p.slug || (p.name ? p.name.toLowerCase().replace(/\s+/g, '-') : `product-${p.id}`),
        description: p.description || '',
        suitableDishes: p.suitableDishes || [],
      }));
    }
    return DEMO_PRODUCTS.map(brandifyProduct);
  } catch {
    return DEMO_PRODUCTS.map(brandifyProduct);
  }
}

export async function fetchProductBySlug(slug: string): Promise<Product | null> {
  const products = await fetchProducts();
  // Names now carry «X-1493»; normalize (strip «», lowercase, spaces->dashes)
  // so product links shared before the rename still resolve.
  const norm = (s: string) =>
    decodeURIComponent(s).toLowerCase().replace(/[«»]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
  const target = norm(slug);
  return (
    products.find((p) => p.slug === slug) ||
    products.find((p) => p.name === slug) ||
    products.find((p) => norm(p.slug) === target) ||
    products.find((p) => norm(p.name) === target) ||
    null
  );
}
